import { Actions } from '@constants/actionTypes';
import { DataKeys } from '@constants/login';
import {
  LOGIN_PAGE_ROUTE,
  MAIN_PAGE_ROUTE,
  MFA_ENFORCED_BY_COMPANY_ROUTE,
  OTP_PAGE_ROUTE,
} from '@constants/routes';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import type { IUserLoginPayload } from '@interfaces/ILogin';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { loginApi } from '../../api/userApi';
import { userDefaults } from '../../redux/initialState';
import { useMainDispatch } from '../useMainDispatch';

interface IUseLogin {
  submitLogin: (formData: IUserLoginPayload) => Promise<void>;
  logout: () => void;
}

export const useLogin = (): IUseLogin => {
  const dispatch = useMainDispatch();
  const navigate = useNavigate();

  const submitLogin = useCallback(
    async (formData: IUserLoginPayload) => {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      const { email, password, token } = formData;

      dispatch({
        type: Actions.SET_USER,
        payload: { email, password },
      });
      try {
        const {
          data: { refreshToken, accessToken, mfaData },
        } = await loginApi(formData);

        if (mfaData) {
          dispatch({
            type: Actions.SET_USER,
            payload: {
              mfaData: {
                qrCode: mfaData.qrCode,
                name: mfaData.name,
                key: mfaData.key,
              },
            },
          });
          navigate(MFA_ENFORCED_BY_COMPANY_ROUTE);
          return;
        }

        localStorage.setItem(DataKeys.ACCESS_TOKEN_DATA_KEY, accessToken);
        localStorage.setItem(DataKeys.REFRESH_TOKEN_DATA_KEY, refreshToken);

        dispatch({
          type: Actions.SET_LOGIN,
          payload: { refreshToken, accessToken, isLoggedIn: true },
        });
        dispatch({
          type: Actions.SET_USER,
          payload: { email: '', password: '' },
        });

        toastSuccessTranslated('auth.login.success.message');

        navigate(MAIN_PAGE_ROUTE);
      } catch (error: any) {
        if (error?.response?.data?.errorCode === 'MFA_ENFORCED_LOGIN_ERROR') {
          navigate(MFA_ENFORCED_BY_COMPANY_ROUTE);
        } else if (!token && error?.response?.data?.errorCode === 'MFA_ERROR') {
          navigate(OTP_PAGE_ROUTE);
        } else if (error?.response?.status === 429) {
          toastErrorTranslated('error.codes.TOO_MANY_REQUESTS');
          navigate(LOGIN_PAGE_ROUTE);
        } else {
          toastErrorTranslated('auth.login.fail.message');
          navigate(LOGIN_PAGE_ROUTE);
        }
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, navigate],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(DataKeys.ACCESS_TOKEN_DATA_KEY);
    localStorage.removeItem(DataKeys.REFRESH_TOKEN_DATA_KEY);
    localStorage.removeItem(DataKeys.SELECTED_COMPANY_DATA_KEY);

    dispatch({
      type: Actions.SET_LOGIN,
      payload: { accessToken: '', refreshToken: '', isLoggedIn: false },
    });
    dispatch({
      type: Actions.SET_USER,
      payload: userDefaults,
    });
  }, [dispatch]);

  return { submitLogin, logout };
};
